import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import apiClient from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CheckCircle, XCircle, Warning, CaretRight } from "@phosphor-icons/react";

const MAX_POLLS = 6;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, paid, pending, failed
  const [planId, setPlanId] = useState(null);
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    let polls = 0;
    let timer;

    const check = async () => {
      polls += 1;
      try {
        const res = await apiClient.get(`/payments/status/${sessionId}`);
        setPlanId(res.data.plan_id);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          return;
        }
        if (res.data.status === "expired") {
          setStatus("failed");
          return;
        }
        if (polls >= MAX_POLLS) {
          setStatus("pending");
          return;
        }
        timer = setTimeout(check, POLL_INTERVAL);
      } catch {
        if (polls >= MAX_POLLS) {
          setStatus("failed");
        } else {
          timer = setTimeout(check, POLL_INTERVAL);
        }
      }
    };

    check();
    return () => timer && clearTimeout(timer);
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-obsidian-900 text-white px-4">
      <div className="max-w-md w-full surface rounded-2xl p-10 text-center">
        {status === "checking" && (
          <>
            <div className="w-16 h-16 mx-auto mb-5 border-4 border-white/10 border-t-yellow-400 rounded-full animate-spin" />
            <h1 className="text-display font-black text-2xl tracking-tight">Verificando pago...</h1>
            <p className="text-zinc-400 text-sm mt-2">Esto toma unos segundos.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle weight="fill" className="w-16 h-16 mx-auto mb-5 text-emerald-400" />
            <h1 className="text-display font-black text-3xl tracking-tight" data-testid="payment-success-title">¡Pago confirmado!</h1>
            <p className="text-zinc-400 text-sm mt-3">Tu suscripción está activa. Bienvenido al club.</p>
            <Link to="/dashboard" className="btn-primary mt-6" data-testid="payment-success-cta">
              Ir al Dashboard <CaretRight weight="bold" className="w-4 h-4" />
            </Link>
          </>
        )}
        {status === "pending" && (
          <>
            <Warning weight="fill" className="w-16 h-16 mx-auto mb-5 text-yellow-400" />
            <h1 className="text-display font-black text-2xl tracking-tight">Pago en proceso</h1>
            <p className="text-zinc-400 text-sm mt-3">El pago todavía se está procesando. Se activará automáticamente.</p>
            <Link to="/dashboard" className="btn-secondary mt-6">Ir al Dashboard</Link>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle weight="fill" className="w-16 h-16 mx-auto mb-5 text-red-400" />
            <h1 className="text-display font-black text-2xl tracking-tight">Algo falló</h1>
            <p className="text-zinc-400 text-sm mt-3">No pudimos confirmar el pago. Intenta de nuevo.</p>
            <Link to="/plans" className="btn-primary mt-6">Volver a planes</Link>
          </>
        )}
      </div>
    </div>
  );
}
